import { faArrowDown, faArrowUp, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Col, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import QuantityInput from "../../../../../components/atoms/quantity-input/quantity-input";
import TextAreaAtom from "../../../../../components/atoms/text-area/text-area";
import TextFieldAtom from "../../../../../components/atoms/text-field/text-field";
import { formatQuantityToLocale } from "../../../../../utils/method";

const LineEditorV2 = ({
    line,
    index,
    onLineChange,
    getLineError,
    autoFocus = false,
    showSequenceControls = false,
    onMoveUp,
    onMoveDown,
    onRemove,
    canMoveUp = true,
    canMoveDown = true,
    lineRef = null
}) => {
    const { t } = useTranslation();

    return (
        <div className="line-editor">
            {showSequenceControls && (
                <div className="d-flex justify-content-between mb-2">
                    <h6 className="mb-0">
                        {t("aufmass.groups.line")} #{index + 1}
                    </h6>
                    <div>
                        <OverlayTrigger
                            placement="top"
                            overlay={
                                <Tooltip id={`move-up-tooltip-${line.id}`}>
                                    {t("help.move-up")}
                                </Tooltip>
                            }
                        >
                            <span className="d-inline-block">
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() => onMoveUp && onMoveUp()}
                                    disabled={!canMoveUp}
                                    className="me-1"
                                >
                                    <FontAwesomeIcon icon={faArrowUp} />
                                </Button>
                            </span>
                        </OverlayTrigger>
                        <OverlayTrigger
                            placement="top"
                            overlay={
                                <Tooltip id={`move-down-tooltip-${line.id}`}>
                                    {t("help.move-down")}
                                </Tooltip>
                            }
                        >
                            <span className="d-inline-block">
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() => onMoveDown && onMoveDown()}
                                    disabled={!canMoveDown}
                                    className="me-1"
                                >
                                    <FontAwesomeIcon icon={faArrowDown} />
                                </Button>
                            </span>
                        </OverlayTrigger>
                        <OverlayTrigger
                            placement="top"
                            overlay={
                                <Tooltip id={`delete-tooltip-${line.id}`}>
                                    {t("help.action-delete")}
                                </Tooltip>
                            }
                        >
                            <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => onRemove && onRemove()}
                            >
                                <FontAwesomeIcon icon={faTrash} />
                            </Button>
                        </OverlayTrigger>
                    </div>
                </div>
            )}

            <Row className="mb-3">
                <Col md={2}>
                    <TextFieldAtom
                        id={`line-text-${line.id}`}
                        label={t("aufmass.text")}
                        placeholder={t("aufmass.text")}
                        name="text"
                        value={line?.text || ''}
                        onChange={(e) => onLineChange(index, "text", e.target.value)}
                        error={getLineError ? getLineError(index, "text") : ""}
                        required
                        ref={lineRef}
                        autoFocus={autoFocus}
                    />
                </Col>
                <Col md={4}>
                    <TextFieldAtom
                        id={`line-factor-${line.id}`}
                        label={t("aufmass.factor-arithmetic-operation")}
                        name="factorOperation"
                        value={line.factorOperation}
                        onChange={(e) => onLineChange(index, "factorOperation", e.target.value)}
                        placeholder={t("aufmass.factor-operation-placeholder")}
                        error={getLineError ? getLineError(index, "factorOperation") : ""}
                        required
                        hint={t("aufmass.formula-hint")}
                    />
                </Col>
                <Col md={3}>
                    <TextFieldAtom
                        id={`line-calculated-quantity-${line.id}`}
                        label={t("aufmass.calculated-quantity") + (line.quantityMismatch ? " *" : "")}
                        value={formatQuantityToLocale(line.quantity)}
                        disabled
                        hint={t("aufmass.calculated-quantity-hint")}
                        hintClassName={line.quantityMismatch ? "text-danger" : "text-muted"}
                    />
                </Col>
                <Col md={3}>
                    <QuantityInput
                        id={`line-aufmass-quantity-${line.id}`}
                        label={t("aufmass.aufmass-document-quantity") + (line.quantityMismatch ? " !" : "")}
                        value={line.manualQuantity}
                        onChange={(e) => onLineChange(index, "manualQuantity", e.target.value)}
                        placeholder={t("aufmass.enter-manual-quantity")}
                        error={getLineError ? getLineError(index, "manualQuantity") : ""}
                        required
                        hint={line.quantityMismatch ? t("aufmass.quantity-mismatch-warning") : t("aufmass.manual-quantity-hint")}
                        hintClassName={line.quantityMismatch ? "text-danger" : "text-muted"}
                        decimalPlaces={3}
                    />
                </Col>
            </Row>

            {line.quantityMismatch && (
                <Row className="mb-3">
                    <Col md={12}>
                        <TextAreaAtom
                            id={`line-comments-${line.id}`}
                            label={t("aufmass.comments")}
                            placeholder={t("aufmass.enter-comments-for-mismatch")}
                            value={line?.comments || ''}
                            onChange={(e) => onLineChange(index, "comments", e.target.value)}
                            rows={3}
                        />
                        <small className="text-muted">
                            {t("aufmass.comments-mismatch-hint")}
                        </small>
                    </Col>
                </Row>
            )}
        </div>
    );
};

export default LineEditorV2;
